/**
 * Shared search tokenisation for POS product search.
 *
 * Both the local (IndexedDB) and remote (SQL) search legs must agree on what a
 * word boundary is, or the same keyword returns different results online vs
 * offline. The server mirror of SEARCH_SEPARATORS lives in
 * SaleController::searchProducts() — change one, change both, and keep
 * resources/js/tests/searchTokens.test.js green.
 */

/** Anything that is not a letter or digit separates words. */
export const SEARCH_SEPARATORS = /[^\p{L}\p{N}]+/gu;

/**
 * Lowercased word tokens for a product name.
 * "Colgate (Max Fresh)" -> ['colgate', 'max', 'fresh']
 */
export const tokenizeName = (value) =>
    String(value ?? '')
        .toLowerCase()
        .split(SEARCH_SEPARATORS)
        .filter(Boolean);

/** Normalised search term: trimmed, lowercased, internal whitespace collapsed. */
export const normalizeTerm = (value) =>
    String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

/**
 * Query tokens. Same splitter as tokenizeName so "a/c filter" queries the same
 * way the name "A/C Filter" was indexed.
 */
export const tokenizeTerm = (value) => tokenizeName(value);

/**
 * Does every query token word-prefix-match some token of an indexed name?
 * This is the AND semantics the server implements in SQL.
 */
export const matchesAllTokens = (nameTokens, queryTokens) => {
    if (!queryTokens.length) return false;
    const haystack = Array.isArray(nameTokens) ? nameTokens : [];
    return queryTokens.every((token) =>
        haystack.some((candidate) => candidate.startsWith(token)),
    );
};

/**
 * The most selective token to drive the indexed lookup — longest wins, since a
 * longer prefix scans a narrower slice of the multiEntry index.
 */
export const selectDriverToken = (tokens) =>
    tokens.reduce((longest, token) => (token.length > longest.length ? token : longest), '');
